import { isFeatureEnabled } from '@dadohr/shared/domainTypes/features';
import {
  Integration,
  OrgIntegrations
} from '@dadohr/shared/domainTypes/integrations';
import {
  IntegrationDataMap,
  MergeHRISIntegration
} from '@dadohr/shared/domainTypes/integrations/merge';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { compact, sortBy } from 'lodash';
import React, { createElement, useEffect, useMemo, useState } from 'react';
import { ColoredChip } from '../../../../components/ColoredChip';
import { NoAccess } from '../../../../components/NoAccess';
import { TableSearch } from '../../../../components/Table';
import { TeaserBoxFreeTierForExperiences } from '../../../../components/TeaserBox';
import ENVS from '../../../../env.json';
import {
  UnsafeTFunction,
  useTranslation,
  useUnsafeTranslation
} from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import {
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../services/currentUser';
import { isFreeTierOrg } from '../../../../services/organizationTier';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { useIntegrationsContext } from '../../../../services/integrations/context';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { useOrgSideNav } from '../sideNav';
import { AdpWorkforceNowSettingsButton } from './integrations/adp';
import { AirtableSettingsButtonV2 } from './integrations/airtable';
import { AsanaSettingsButton } from './integrations/asana';
import { AshbySettingsButton } from './integrations/ashby';
import { AtlassianSettingsButton } from './integrations/atlassian';
import { BambooHrSettingsButton } from './integrations/bamboohr';
import { BobSettingsButton } from './integrations/bob';
import { CalendlySettingsButton } from './integrations/calendly';
import { ChartHopSettingsButton } from './integrations/charthop';
import { CheckrSettingsButton } from './integrations/checkr';
import { SalesforceSettingsButton } from './integrations/salesforce';
import { useConfigInfos } from './integrations/common';
import { DocuSignSettingsButton } from './integrations/docusign';
import { GoogleSheetsSettingsButton } from './integrations/googleSheets';
import { GreenhouseV3SettingsButton } from './integrations/greenhouseV3';
import { LessonlySettingsButton } from './integrations/lessonly';
import { DoceboSettingsButton } from './integrations/docebo';
import { MergeSettingsButton } from './integrations/merge';
import { MicrosoftSettingsButton } from './integrations/microsoft';
import { MicrosoftEntraSsoSettingsButton } from './integrations/microsoft/entra_sso';
import { OktaSettingsButton } from './integrations/okta';
import { OktaApiSettingsButton } from './integrations/okta_api';
import { PersonioSettingsButton } from './integrations/personio';
import { PingboardSettingsButton } from './integrations/pingboard';
import { HireRightSettingsButton } from './integrations/hireright';
import { RipplingSettingsButton } from './integrations/rippling';
import { SamlSettingsButton } from './integrations/saml';
import { EmailCsvSettingsButton } from './integrations/emailCsv';
import { SftpSettingsButton } from './integrations/sftp';
import { SlackSettingsButton } from './integrations/slack';
import { SwagUpSettingsButton } from './integrations/swagUp';
import { WorkdayRaasSettingsButton } from './integrations/workday';
import { WorkdayApiSettingsButton } from './integrations/workday-api';
import {
  UKG_CARD_NAME,
  UkgProSettingsButton
} from './integrations/ukg';
import { UKG_INTEGRATION_TYPE } from './integrations/ukg/service';

export enum Functionality {
  IMPORT_SOURCE = 'IMPORT_SOURCE',
  ACCESS_MANAGEMENT = 'ACCESS_MANAGEMENT',
  AUTOMATIONS = 'AUTOMATIONS',
  DATA_DESTINATION = 'DATA_DESTINATION',
  ADDITIONAL_SUBTASK = 'ADDITIONAL_SUBTASK',
  NOTIFICATION_CHANNEL = 'NOTIFICATION_CHANNEL'
}

export const FunctionalityLabels: {
  [key in Functionality]: (t: UnsafeTFunction) => string;
} = {
  [Functionality.IMPORT_SOURCE]: (t) => t('Import source'),
  [Functionality.ACCESS_MANAGEMENT]: (t) => t('Access management'),
  [Functionality.AUTOMATIONS]: (t) => t('Automation'),
  [Functionality.DATA_DESTINATION]: (t) => t('Data destination'),
  [Functionality.ADDITIONAL_SUBTASK]: (t) => t('Additional subtask'),
  [Functionality.NOTIFICATION_CHANNEL]: (t) => t('Notification channel')
};

export const FunctionalityLabelsPlural: {
  [key in Functionality]: (t: UnsafeTFunction) => string;
} = {
  [Functionality.IMPORT_SOURCE]: (t) => t('Import sources'),
  [Functionality.ACCESS_MANAGEMENT]: (t) => t('Access management'),
  [Functionality.AUTOMATIONS]: (t) => t('Automations'),
  [Functionality.DATA_DESTINATION]: (t) => t('Data destinations'),
  [Functionality.ADDITIONAL_SUBTASK]: (t) => t('Additional subtasks'),
  [Functionality.NOTIFICATION_CHANNEL]: (t) => t('Notification channels')
};

interface IIntegration {
  key: string;
  mergeHrisIntegration?: MergeHRISIntegration;
  type: Integration;
  displayName: string;
  enabled: (org: IOrganization, isDadoEmployee: boolean) => boolean;
  functionalities: Functionality[];
  component: React.ComponentType<{
    functionalities: Functionality[];
    interactive: boolean;
  }>;
}

export const INTEGRATIONS: IIntegration[] = compact([
  {
    key: 'Airtable',
    type: Integration.AIRTABLE,
    displayName: 'Airtable',
    enabled: () => true,
    functionalities: [
      Functionality.DATA_DESTINATION,
      Functionality.IMPORT_SOURCE
    ],
    component: AirtableSettingsButtonV2
  },
  {
    key: 'AdpWorkforceNow',
    type: Integration.ADP_WORKFORCE_NOW,
    displayName: 'ADP Workforce Now',
    enabled: () => true,
    functionalities: [
      Functionality.IMPORT_SOURCE,
      Functionality.DATA_DESTINATION
    ],
    component: AdpWorkforceNowSettingsButton
  },
  {
    key: 'Asana',
    type: Integration.ASANA,
    displayName: 'Asana',
    enabled: () => true,
    functionalities: [Functionality.AUTOMATIONS],
    component: AsanaSettingsButton
  },
  {
    key: 'Ashby',
    type: Integration.ASHBY,
    displayName: 'Ashby',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: AshbySettingsButton
  },
  {
    key: 'BambooHr',
    type: Integration.BAMBOOHR,
    displayName: 'BambooHr',
    enabled: () => true,
    functionalities: [
      Functionality.DATA_DESTINATION,
      Functionality.IMPORT_SOURCE
    ],
    component: BambooHrSettingsButton
  },
  {
    key: 'Bob',
    type: Integration.BOB,
    displayName: 'Bob',
    enabled: () => true,
    functionalities: [
      Functionality.DATA_DESTINATION,
      Functionality.IMPORT_SOURCE
    ],
    component: BobSettingsButton
  },
  {
    key: 'Calendly',
    type: Integration.CALENDLY,
    displayName: 'Calendly',
    enabled: () => true,
    functionalities: [Functionality.ADDITIONAL_SUBTASK],
    component: CalendlySettingsButton
  },

  {
    key: 'ChartHop',
    displayName: 'ChartHop (updated)',
    type: Integration.CHARTHOP,
    enabled: () => true,
    component: ChartHopSettingsButton,
    functionalities: [Functionality.IMPORT_SOURCE]
  },
  {
    key: 'DocuSign',
    type: Integration.DOCUSIGN,
    displayName: 'DocuSign',
    enabled: () => true,
    functionalities: [Functionality.ADDITIONAL_SUBTASK],
    component: DocuSignSettingsButton
  },
  {
    key: 'Google Sheets',
    type: Integration.GOOGLE_SHEETS,
    displayName: 'Google  Workspace',
    enabled: () => true,
    functionalities: [
      Functionality.DATA_DESTINATION,
      Functionality.IMPORT_SOURCE,
      Functionality.AUTOMATIONS
    ],
    component: GoogleSheetsSettingsButton
  },
  {
    key: 'Greenhouse',
    type: Integration.GREENHOUSE_V3,
    displayName: 'Greenhouse',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: GreenhouseV3SettingsButton
  },
  {
    key: 'Checkr',
    type: Integration.CHECKR,
    displayName: 'Checkr',
    enabled: (org) => isFeatureEnabled(org.features, 'CHECKR_V1'),
    functionalities: [Functionality.ADDITIONAL_SUBTASK],
    component: CheckrSettingsButton
  },
  {
    key: 'Salesforce',
    type: Integration.SALESFORCE,
    displayName: 'Salesforce',
    enabled: (org) => isFeatureEnabled(org.features, 'SALESFORCE_V1'),
    functionalities: [
      Functionality.DATA_DESTINATION,
      Functionality.IMPORT_SOURCE
    ],
    component: SalesforceSettingsButton
  },
  {
    key: 'HireRight',
    type: Integration.HIRERIGHT,
    displayName: 'HireRight',
    enabled: () => true,
    functionalities: [Functionality.AUTOMATIONS, Functionality.ADDITIONAL_SUBTASK],
    component: HireRightSettingsButton
  },
  {
    key: 'Jira',
    enabled: () => true,
    type: Integration.ATLASSIAN,
    displayName: 'Jira',
    functionalities: [Functionality.AUTOMATIONS],
    component: AtlassianSettingsButton
  },
  {
    key: 'Lessonly',
    type: Integration.LESSONLY,
    displayName: 'Lessonly',
    enabled: () => true,
    functionalities: [Functionality.AUTOMATIONS],
    component: LessonlySettingsButton
  },
  {
    key: 'Docebo',
    type: Integration.DOCEBO,
    displayName: 'Docebo',
    enabled: () => true,
    functionalities: [Functionality.AUTOMATIONS],
    component: DoceboSettingsButton
  },
  {
    key: 'Microsoft',
    type: Integration.MICROSOFT,
    displayName: 'Microsoft',
    enabled: () => true,
    functionalities: [
      Functionality.ADDITIONAL_SUBTASK,
      Functionality.DATA_DESTINATION,
      Functionality.NOTIFICATION_CHANNEL
    ],
    component: MicrosoftSettingsButton
  },

  {
    key: 'Microsoft Entra SSO',
    type: Integration.MICROSOFT_ENTRA_SSO,
    displayName: 'Microsoft Entra SSO',
    enabled: () => true,
    functionalities: [Functionality.ACCESS_MANAGEMENT],
    component: MicrosoftEntraSsoSettingsButton
  },
  {
    key: 'Okta SSO',
    type: Integration.OKTA,
    displayName: 'Okta SSO',
    enabled: () => true,
    functionalities: [Functionality.ACCESS_MANAGEMENT],
    component: OktaSettingsButton
  },
  {
    key: 'Okta API',
    type: Integration.OKTA_API,
    displayName: 'Okta API',
    enabled: () => true,
    functionalities: [Functionality.AUTOMATIONS],
    component: OktaApiSettingsButton
  },
  {
    key: 'Personio',
    type: Integration.PERSONIO,
    displayName: 'Personio',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: PersonioSettingsButton
  },
  {
    key: 'Pingboard',
    type: Integration.PINGBOARD,
    displayName: 'Pingboard',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: PingboardSettingsButton
  },
  {
    key: 'Rippling',
    type: Integration.RIPPLING,
    displayName: 'Rippling',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: RipplingSettingsButton
  },
  {
    key: 'SAML SSO',
    type: Integration.OKTA,
    displayName: 'SAML SSO Provider',
    enabled: () => true,
    functionalities: [Functionality.ACCESS_MANAGEMENT],
    component: SamlSettingsButton
  },

  {
    key: 'Slack',
    type: Integration.SLACK,
    displayName: 'Slack',
    enabled: () => true,
    functionalities: [
      Functionality.AUTOMATIONS,
      Functionality.NOTIFICATION_CHANNEL
    ],
    component: SlackSettingsButton
  },
  {
    key: 'SFTP',
    type: Integration.SFTP,
    displayName: 'SFTP',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: SftpSettingsButton
  },
  {
    key: 'EMAIL_CSV',
    type: Integration.EMAIL_CSV,
    displayName: 'Email CSV',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: EmailCsvSettingsButton
  },
  {
    key: 'SwagUp',
    type: Integration.SWAG_UP,
    displayName: 'SwagUp',
    enabled: () => true,
    functionalities: [Functionality.ADDITIONAL_SUBTASK],
    component: SwagUpSettingsButton
  },
  {
    key: 'Workday RaaS',
    type: Integration.WORKDAY,
    displayName: 'Workday RaaS',
    enabled: () => true,
    functionalities: [Functionality.IMPORT_SOURCE],
    component: WorkdayRaasSettingsButton
  },
  {
    key: 'Workday API',
    type: Integration.WORKDAY_API,
    displayName: 'Workday API',
    enabled: () => true,
    functionalities: [Functionality.DATA_DESTINATION],
    component: WorkdayApiSettingsButton
  },
  {
    key: 'UkgPro',
    type: UKG_INTEGRATION_TYPE,
    // Same string the card itself renders — see UKG_CARD_NAME for why it is
    // not just 'UKG Pro'.
    displayName: UKG_CARD_NAME,
    // Dark-launched per org: UKG auth is validated against live customer
    // tenants (no sandbox), and this card can write back to UKG, so the flag
    // defaults to false and is turned on one org at a time.
    enabled: (org) => isFeatureEnabled(org.features, 'UKG_V1'),
    functionalities: [
      Functionality.IMPORT_SOURCE,
      Functionality.DATA_DESTINATION
    ],
    component: UkgProSettingsButton
  }
]);

export const MERGE_HRIS_INTEGRATIONS: IIntegration[] = Object.values(
  MergeHRISIntegration
)
  .filter((mi) => {
    if (mi === MergeHRISIntegration.RIPPLING) {
      return false;
    }
    if (!ENVS.env.safe && mi === MergeHRISIntegration.BAMBOOHR) {
      return false;
    } else if (mi === MergeHRISIntegration.PERSONIO) {
      return false;
    }

    // if (
    //   SHOW_CHARTHOP &&
    //   !ENVS.env.safe &&
    //   mi === MergeHRISIntegration.CHARTHOP
    // ) {
    //   return false;
    // }

    return true;
  })
  .map((mi) => {
    const integrations: IIntegration = {
      key: 'merge-' + mi,
      mergeHrisIntegration: mi,
      type: Integration.MERGE,
      displayName: IntegrationDataMap[mi].name,
      enabled: () => true,
      functionalities: [Functionality.IMPORT_SOURCE],
      component: ({
        functionalities,
        interactive
      }: {
        functionalities: Functionality[];
        interactive: boolean;
      }) => (
        <MergeSettingsButton
          hris={mi}
          functionalities={functionalities}
          interactive={interactive}
        />
      )
    };
    return integrations;
  });


export const ALL_INTEGRATIONS = sortBy(
  [
    ...INTEGRATIONS,
    ...MERGE_HRIS_INTEGRATIONS
  ],
  (integration) => integration.displayName
);

export const isActiveIntegration = (
  // null/undefined when the org has never set up any integration — Firestore
  // returns no doc and useConfigInfos gives back undefined. Callers used to
  // guard at the call site, but that produced a confusing UX: turning Active
  // ON appeared to be a no-op because every integration passed the filter.
  // Treat missing configInfo the same way as "no entries in configInfos" —
  // the answer is "nothing active" except for whatever the runtime context
  // independently reports as active.
  configInfo: OrgIntegrations | null | undefined,
  integration: IIntegration,
  // Runtime active set from IntegrationsContext — fallback source-of-truth for
  // integrations that don't write to `integrations_config_infoV1`. Several
  // FE-visible integrations (Checkr, SAML SSO, HireRight, Email CSV, Rippling,
  // Workday API, Microsoft Entra SSO) have `null` in
  // `functions/integrations-config-manager` CONFIG_COLLECTIONS, so their
  // `configInfos[type].active` never gets set even when the integration IS
  // connected. The subtask builder already trusts this set (e.g.
  // `activeIntegrations.has(Integration.SALESFORCE)`), so aligning the
  // Integrations page filter with the same source eliminates the discrepancy.
  activeIntegrationsSet?: ReadonlySet<Integration>
) => {
  if (integration.type === Integration.MERGE) {
    const activeIntegrations =
      configInfo?.configInfos[Integration.MERGE]?.activeHRIS;
    return (
      integration.mergeHrisIntegration &&
      activeIntegrations &&
      activeIntegrations.includes(integration.mergeHrisIntegration)
    );
  }
  return (
    configInfo?.configInfos[integration.type]?.active ||
    activeIntegrationsSet?.has(integration.type) ||
    false
  );
};

const IntegrationsGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))',
  gridColumnGap: p.theme.spacing(2),
  gridRowGap: p.theme.spacing(2)
}));

const toSearchTermRegexp = (stringQuery: string) =>
  new RegExp(stringQuery, 'i');

const EmptyState = ({ onResetFilters }: { onResetFilters: () => any }) => {
  const { t } = useUnsafeTranslation();

  return (
    <Centered>
      {t('No results for these filters.')}
      <Button variant="text" onClick={() => onResetFilters()}>
        {t('Reset filters')}
      </Button>
    </Centered>
  );
};

const Integrations = ({
  org,
  activeFunctionalities,
  stringQuery,
  onResetFilters,
  activeFilterEnabled
}: {
  org: IOrganization;
  activeFilterEnabled: boolean;
  activeFunctionalities: Functionality[];
  stringQuery: string;
  onResetFilters: () => void;
}) => {
  const isDadoEmployee = useIsCurrentUserDadoEmployee();
  const [configInfosDoc] = useConfigInfos(org.id);
  const configInfo = configInfosDoc?.data;
  const { active: activeIntegrationsSet } = useIntegrationsContext();

  const enabledIntegrations = useMemo(() => {
    return ALL_INTEGRATIONS.filter((i) => i.enabled(org, isDadoEmployee));
  }, [org, isDadoEmployee]);

  const visisbleIntegrations = useMemo(() => {
    if (
      activeFunctionalities.length <= 0 &&
      !stringQuery &&
      !activeFilterEnabled
    ) {
      return enabledIntegrations;
    }
    return enabledIntegrations.filter((i) => {
      const hasActiveFuntionality =
        activeFunctionalities.length <= 0 ||
        !!i.functionalities.find((f) => activeFunctionalities.includes(f));

      const matchesStringQuery =
        !stringQuery ||
        toSearchTermRegexp(stringQuery).exec(i.displayName || i.key);

      // When Active filter is on, ask isActiveIntegration unconditionally.
      // The function now handles missing configInfo gracefully and falls back
      // to the runtime IntegrationsContext set. The old "configInfo &&" guard
      // made the filter no-op for orgs with no integrations doc — confusing
      // UX (turning Active ON appeared to do nothing).
      const fulfillsActive =
        !activeFilterEnabled ||
        isActiveIntegration(configInfo, i, activeIntegrationsSet);

      return hasActiveFuntionality && matchesStringQuery && fulfillsActive;
    });
  }, [
    enabledIntegrations,
    stringQuery,
    activeFunctionalities,
    activeFilterEnabled,
    configInfo,
    activeIntegrationsSet
  ]);

  return (
    <>
      {visisbleIntegrations.length > 0 && (
        <IntegrationsGrid>
          {visisbleIntegrations.map((integration) => (
            <React.Fragment key={integration.key}>
              {createElement(integration.component, {
                functionalities: integration.functionalities,
                interactive: !isFreeTierOrg(org)
              })}
            </React.Fragment>
          ))}
        </IntegrationsGrid>
      )}
      {visisbleIntegrations.length <= 0 && (
        <EmptyState onResetFilters={onResetFilters} />
      )}
    </>
  );
};

export const BadgeFilterContainer = styled('div')((p) => ({
  position: 'sticky',
  display: 'flex',
  flexWrap: 'wrap',
  top: p.theme.spacing(16),
  zIndex: 4,
  background: p.theme.palette.background.paper,
  paddingBottom: p.theme.spacing(2)
}));

const ClickableBadgeContainer = styled('div')((p) => ({
  cursor: 'pointer',
  padding: p.theme.spacing(1),
  paddingLeft: 0
}));

const FunctionalityFilterButton = ({
  functionality,
  enabled,
  onToggle
}: {
  functionality: Functionality;
  enabled: boolean;
  onToggle: (enabled: boolean, functionality: Functionality) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <ClickableBadgeContainer onClick={() => onToggle(!enabled, functionality)}>
      <ColoredChip color={enabled ? 'darkGray' : 'gray'}>
        {FunctionalityLabelsPlural[functionality](t)}
      </ColoredChip>
    </ClickableBadgeContainer>
  );
};

const ActiveFilterButton = ({
  enabled,
  onToggle
}: {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <ClickableBadgeContainer onClick={() => onToggle(!enabled)}>
      <ColoredChip color="green" mode={enabled ? 'normal' : 'light'}>
        {t('Active')}
      </ColoredChip>
    </ClickableBadgeContainer>
  );
};

const IntegrationsToolbarTitle = styled(PageToolbarTitle)`
  flex-direction: column;
`;

const IntegrationsSearch = styled(PageToolbarActions)`
  align-self: start;
`;

const ContentOrgIntegrations = () => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const sideNav = useOrgSideNav();
  const [stringQuery, setStringQuery] = useStringQueryParam('q', '');

  const { trackEvent } = useProductAnalytics();
  useEffect(() => {
    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'Integrations Page Loaded',
      Explanation: 'Admin user loads the integrations settings page'
    });
  }, [trackEvent]);

  const [activeFunctionalities, setActiveFunctionalities] = useState<
    Functionality[]
  >([]);

  const setFunctionalityState = (
    enabled: boolean,
    functionality: Functionality
  ) => {
    setActiveFunctionalities((prevFuncs) =>
      enabled
        ? [...prevFuncs, functionality]
        : prevFuncs.filter((f) => f !== functionality)
    );
  };

  const [activeFilterEnabled, setActiveFilterEnabled] = useState(false);

  const resetFilters = () => {
    setStringQuery('');
    setActiveFunctionalities([]);
    setActiveFilterEnabled(false);
  };

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <TeaserBoxFreeTierForExperiences>
        {ut(
          'This is a preview of the types of integrations we offer for Experiences.'
        )}
      </TeaserBoxFreeTierForExperiences>
      <PageToolbar sticky>
        <IntegrationsToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgIntegrations')}
          </Typography>
        </IntegrationsToolbarTitle>
        <IntegrationsSearch>
          <TableSearch
            term={stringQuery}
            onChange={setStringQuery}
            autoFocus
            placeholder={t('users.overview.search')}
          />
        </IntegrationsSearch>
      </PageToolbar>

      <Section>
        <BadgeFilterContainer>
          <ActiveFilterButton
            enabled={activeFilterEnabled}
            onToggle={setActiveFilterEnabled}
          />
          {Object.values(Functionality).map((f) => (
            <FunctionalityFilterButton
              key={f}
              enabled={activeFunctionalities.includes(f)}
              functionality={f}
              onToggle={setFunctionalityState}
            />
          ))}
        </BadgeFilterContainer>
        <Integrations
          org={org}
          activeFilterEnabled={activeFilterEnabled}
          activeFunctionalities={activeFunctionalities}
          stringQuery={stringQuery}
          onResetFilters={resetFilters}
        />
      </Section>
    </PageBody>
  );
};

export const PageOrgIntegrations = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.org.integrations)) {
    return <NoAccess />;
  }

  return <ContentOrgIntegrations />;
};
